import React, { useEffect, useState, useTransition } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { updateUserProfile } from "../../../services/server-actions";
import CalendarModal from "./DatePickerModal";
import styles from "./InfoPage.module.css";
import { DateTime } from "ts-luxon";
import { MobileCustomerProfileDto } from "../../../services/shared/service-proxies/service-proxies";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/icons/arrowLeft.svg";
import { gregorianToJalali, jalaliToGregorian } from "./date-converter"; // ✅ ایمپورت تابع‌های تبدیل

const ERROR_MESSAGES = {
  firstname: "نام الزامی است",
  lastname: "نام خانوادگی الزامی است",
  sex: "انتخاب جنسیت الزامی است",
  birthday: "تاریخ تولد الزامی است",
};

const schema = Yup.object({
  firstname: Yup.string().required(ERROR_MESSAGES.firstname),
  lastname: Yup.string().required(ERROR_MESSAGES.lastname),
  sex: Yup.number()
    .typeError(ERROR_MESSAGES.sex)
    .required(ERROR_MESSAGES.sex)
    .oneOf([1, 2], "لطفاً جنسیت خود را انتخاب کنید"),
  birthday: Yup.string().required(ERROR_MESSAGES.birthday),
});

interface FormData {
  firstname: string;
  lastname: string;
  sex: number;
  birthday: string; // شمسی yyyy-MM-dd
}

const InfoForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [isPending, startTransition] = useTransition();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isGenderOpen, setGenderOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const storedProfile = JSON.parse(
      localStorage.getItem("user-profile") || "{}"
    );

    if (storedProfile) {
      const { firstname, lastname, sex, birthday } = storedProfile;
      setValue("firstname", firstname || "");
      setValue("lastname", lastname || "");
      setValue("sex", sex || "");

      if (birthday && DateTime.fromISO(birthday).isValid) {
        const gregorianDate = DateTime.fromISO(birthday);
      
        if (gregorianDate.isValid && gregorianDate.year > 1900) { // جلوگیری از سال منفی یا ناقص
          const { jy, jm, jd } = gregorianToJalali(
            gregorianDate.year,
            gregorianDate.month,
            gregorianDate.day
          );
      
          const jalaliDate = `${jy}-${String(jm).padStart(2, "0")}-${String(jd).padStart(2, "0")}`;
          setValue("birthday", jalaliDate);
          setSelectedDate(jalaliDate);
        } else {
          setValue("birthday", "");
          setSelectedDate(""); // نمایش placeholder
        }
      } else {
        setValue("birthday", "");
        setSelectedDate(""); // نمایش placeholder
      }
      
      
    }
  }, [setValue]);

  const toggleCalendar = () => setCalendarOpen((prev) => !prev);

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    setValue("birthday", date);
  };

  const onSubmit: SubmitHandler<FormData> = (data) => {
    startTransition(() => {
      setErrorMessage(null);
      setIsConfirmed(false);

      const [jy, jm, jd] = data.birthday.split("-").map(Number);
      const { gy, gm, gd } = jalaliToGregorian(jy, jm, jd);

      const gregorianBirthday = `${gy}-${String(gm).padStart(2, "0")}-${String(
        gd
      ).padStart(2, "0")}`;

      const updatedProfile = new MobileCustomerProfileDto({
        ...data,
        birthday: DateTime.fromISO(gregorianBirthday),
        phone: "",
      });

      const storedProfile = JSON.parse(
        localStorage.getItem("user-profile") || "{}"
      );

      const hasChanges = Object.keys(data).some(
        (key) => data[key as keyof FormData] !== storedProfile[key]
      );

      if (hasChanges) {
        updateUserProfile(updatedProfile)
          .then(() => {
            setIsConfirmed(true);

            const userProfileForStorage = {
              firstname: updatedProfile.firstname,
              lastname: updatedProfile.lastname,
              sex: updatedProfile.sex,
              birthday: updatedProfile.birthday.toISO(),
              phone: storedProfile.phone || "",
            };

            localStorage.setItem(
              "user-profile",
              JSON.stringify(userProfileForStorage)
            );

            setTimeout(() => navigate("/profile"), 2000);
          })
          .catch(() =>
            setErrorMessage("❌ خطایی رخ داد. لطفاً دوباره تلاش کنید.")
          );
      } else {
        setErrorMessage("اطلاعات بدون تغییر هستند.");
      }
    });
  };

  return (
    <form className={styles.infoForm} onSubmit={handleSubmit(onSubmit)}>
      <p className={styles.infoText}>
        جهت تکمیل پروفایل لطفا اطلاعات زیر را وارد کنید
      </p>

      {["firstname", "lastname"].map((field) => (
        <div className={styles.field} key={field}>
          <input
            type="text"
            placeholder={field === "firstname" ? "نام" : "نام خانوادگی"}
            {...register(field as "firstname" | "lastname")}
            className={`${styles.input} ${styles[field]}`}
          />
          {errors[field as "firstname" | "lastname"] && (
            <span className={styles.error}>
              {errors[field as "firstname" | "lastname"]?.message}
            </span>
          )}
        </div>
      ))}

      <div>
        <div className={styles.field}>
          <select
            {...register("sex", {
              valueAsNumber: true,
              onChange: (e) => {
                setGenderOpen(false);
                e.target.blur();
              },
            })}
            className={`${styles.input} ${styles.select}`}
            onFocus={() => setGenderOpen(true)}
            onBlur={() => setGenderOpen(false)}
          >
            <option value="">جنسیت</option>
            <option value={1}>مرد</option>
            <option value={2}>زن</option>
          </select>

          <ArrowLeftIcon
            className={`${styles.chevronIcon} ${
              isGenderOpen ? styles.rotateUp : styles.rotatedChevron
            }`}
          />
        </div>

        {/* ارور جدا و بعد از field */}
        {errors.sex && (
          <span className={styles.error}>{errors.sex.message}</span>
        )}
      </div>

      <div className={styles.field}>
      <input
  type="text"
  placeholder="تاریخ تولد"
  value={selectedDate}
  readOnly
  onClick={toggleCalendar}
  className={`${styles.input} ${styles.birthday}`}
/>


        <ArrowLeftIcon
          className={`${styles.chevronIcon} ${
            isCalendarOpen ? styles.rotateUp : styles.rotatedChevron
          }`}
        />
        {errors.birthday && (
          <span className={styles.error}>{errors.birthday.message}</span>
        )}
      </div>

      <button
        type="submit"
        className={`${styles.submitButton} ${
          isConfirmed ? styles.confirmed : ""
        }`}
        disabled={isPending}
      >
        {isPending
          ? "در حال ارسال..."
          : isConfirmed
          ? "✅ اطلاعات ثبت شد"
          : "ثبت اطلاعات"}
      </button>

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <CalendarModal
        isOpen={isCalendarOpen}
        onClose={toggleCalendar}
        onSelectDate={handleDateSelect}
      />
    </form>
  );
};

export default InfoForm;
